import { _ as __rest } from './tslib.es6-DN65qNZS.js';
import React, { useState, useMemo, useRef } from 'react';
import { s as styles } from './DataList-DeTUdKxc.js';
import { a as DataListTotalCount } from './DataListTotalCount-BE--Zrik.js';
import { a as DataListLoadingState } from './DataListLoadingState-CNje6Tfl.js';
import { D as DataListLayout } from './DataListLayout-DZmdKXFI.js';
import { D as DataListHeader } from './DataListHeader-pm8YoWDP.js';
import { I as InternalDataListFilters, D as DataListFilters } from './DataListFilters-CRCYSGyC.js';
import { D as DataListStickyHeader } from './DataListStickyHeader-qpV5LAtg.js';
import { I as InternalDataListSearch, a as DataListSearch } from './DataListSearch-DCLwvt_B.js';
import { I as InternalDataListEmptyState, D as DataListEmptyState } from './DataListEmptyState-BD7Y0YSf.js';
import { D as DataListLoadMore } from './DataListLoadMore-QPPSA_-A.js';
import { D as DataListItemActions } from './DataListItemActions-PJA3ny4I.js';
import { D as DataListAction } from './DataListAction-6QAFyj9K.js';
import { D as DataListLayoutActions } from './DataListLayoutActions-CCwY9RBm.js';
import { D as DataListContext, u as useDataListContext } from './DataListContext-CyU4jeBU.js';
import { s as sortBreakpoints, g as getCompoundComponent, a as getCompoundComponents } from './DataList.utils-CKHBAG6U.js';
import { D as DATA_LIST_FILTERING_SPINNER_TEST_ID } from './DataList.const-CjTyAhOC.js';
import { D as DataListBulkActions } from './DataListBulkActions-CHEE3jkL.js';
import { I as InternalDataListStatusBar, D as DataListStatusBar } from './DataListStatusBar-D9h4up_P.js';
import { H as Heading } from './Heading-DX1jjqTV.js';
import { S as Spinner } from './Spinner---cbFcCd.js';

function DataList(_a) {
    var { selected = [], sorting } = _a, props = __rest(_a, ["selected", "sorting"]);
    const [layouts, setLayouts] = useState({});
    const layoutBreakpoints = useMemo(() => sortBreakpoints(Object.keys(layouts)), [layouts]);
    const searchComponent = getCompoundComponent(props.children, DataListSearch);
    const filterComponent = getCompoundComponent(props.children, DataListFilters);
    const layoutComponents = getCompoundComponents(props.children, DataListLayout);
    const emptyStateComponents = getCompoundComponents(props.children, DataListEmptyState);
    const itemActionComponent = getCompoundComponent(props.children, DataListItemActions);
    const bulkActionsComponent = getCompoundComponent(props.children, DataListBulkActions);
    const headerCount = Object.keys(props.headers).length;
    const shouldRenderStickyHeader = !!filterComponent || !!searchComponent || headerCount > 0;
    return (React.createElement(DataListContext.Provider, { value: Object.assign(Object.assign({ searchComponent,
            filterComponent,
            layoutComponents,
            emptyStateComponents,
            itemActionComponent,
            bulkActionsComponent,
            layoutBreakpoints,
            layouts,
            registerLayout }, props), { selected, 
            // T !== DataListObject
            sorting: sorting }) },
        React.createElement(InternalDataList, { shouldRenderStickyHeader: shouldRenderStickyHeader })));
    function registerLayout(size, children) {
        setLayouts(prev => (Object.assign(Object.assign({}, prev), { [size]: children })));
    }
}
function InternalDataList({ shouldRenderStickyHeader, }) {
    const { data, title, totalCount, loadingState = "none", layoutComponents, } = useDataListContext();
    const backToTopRef = useRef(null);
    const initialLoading = loadingState === "initial";
    const showEmptyState = !initialLoading && data.length === 0;
    const shouldRenderLoadMoreTrigger = !initialLoading && !showEmptyState;
    return (React.createElement("div", { className: styles.wrapper },
        React.createElement("div", { className: styles.titleContainer },
            title && React.createElement(Heading, { level: 3 }, title),
            React.createElement(DataListTotalCount, { totalCount: totalCount, loading: initialLoading })),
        React.createElement("div", { ref: backToTopRef }),
        shouldRenderStickyHeader && (React.createElement(DataListStickyHeader, null,
            React.createElement("div", { className: styles.headerFilters },
                React.createElement(InternalDataListFilters, null),
                React.createElement(InternalDataListSearch, null)),
            React.createElement(InternalDataListStatusBar, null),
            React.createElement(DataListHeader, null))),
        initialLoading && React.createElement(DataListLoadingState, null),
        showEmptyState && React.createElement(InternalDataListEmptyState, null),
        layoutComponents,
        loadingState === "filtering" && (React.createElement("div", { "data-testid": DATA_LIST_FILTERING_SPINNER_TEST_ID, className: styles.filtering },
            React.createElement("div", { className: styles.filteringSpinner },
                React.createElement(Spinner, { size: "small" })))),
        shouldRenderLoadMoreTrigger && (React.createElement(DataListLoadMore, { onBackToTop: handleBackToTop }))));
    function handleBackToTop() {
        var _a;
        // For testing purposes since jest doesn't know scrollIntoView.
        // This prevents consumer's tests from needing to mock scrollIntoView.
        if (!window.HTMLElement.prototype.scrollIntoView)
            return;
        (_a = backToTopRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
        });
    }
}
/**
 * Sets the layout that the DataList item and header will use.
 */
DataList.Layout = DataListLayout;
/**
 * By using this component, we can render the actions anywhere in the layout
 * instead of being added automatically on hover.
 */
DataList.LayoutActions = DataListLayoutActions;
/**
 * When the DataList is either empty and/or filtered, this component will be
 * rendered.
 */
DataList.EmptyState = DataListEmptyState;
/**
 * Adds the filter components of your choosing to the DataList.
 */
DataList.Filters = DataListFilters;
/**
 * Enables the search functionality of the DataList.
 *
 * Since the debounce is implemented within the component, it can only be an
 * uncontrolled component. Thus the lack of a `value` prop.
 */
DataList.Search = DataListSearch;
/**
 * Defines the group actions you could do on a single DataList item.
 */
DataList.ItemActions = DataListItemActions;
/**
 * Defines the action in a DataList. This should be used inside the
 * DataListItemActions component.
 */
DataList.ItemAction = DataListAction;
/**
 * Defines the group actions you could do on multiple DataList items.
 */
DataList.BatchActions = DataListBulkActions;
/**
 * Defines the batch action in a DataList. This should be used inside the
 * DataListBatchActions component.
 */
DataList.BatchAction = function DataListBatchAction(props) {
    return React.createElement(DataListAction, Object.assign({}, props));
};
/**
 * Defines a status bar that is rendered between the filters and the header.
 */
DataList.StatusBar = DataListStatusBar;

export { DataList as D };
